/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * MatterUpdatesDetailPage - Full entity-level change detail for a single matter.
 *
 * Shows all pending entity changes grouped by entity type (providers, treatment
 * requests, coverages, tasks, task updates, staff notes, documents) with
 * field-level diffs highlighted based on backend-provided changed_fields.
 *
 * Floating action buttons appear on hover to mark changes as transferred or skipped.
 */

import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ExternalLink, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MatterLink } from '@/components/entity-links';
import { Switch } from '@/components/ui/catalyst/switch';
import { cn } from '@/lib/utils';
import type { EntityChangeSchema, EntityChangeStatus } from '@/services/api/generated/entityChangesService';
import {
  useEntityChangesBulkUpdateStatusMutation,
  useEntityChangesMatterDetailQuery,
} from '@/services/api/generated/entityChangesService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';

import { CaseExpenseChangeSection } from './components/CaseExpenseChangeSection';
import { CoverageChangeSection } from './components/CoverageChangeSection';
import {
  DetailDrawerProvider,
  useDetailDrawer,
} from './components/DetailDrawerContext';
import { DocumentChangeSection } from './components/DocumentChangeSection';
import {
  type EntityChangeActionsContextValue,
  EntityChangeActionsProvider,
} from './components/EntityChangeActionsContext';
import { EntitySyncAccordionSection } from './components/EntitySyncAccordionSection';
import { ProviderChangeSection } from './components/ProviderChangeSection';
import { StaffNoteChangeSection } from './components/StaffNoteChangeSection';
import { TaskChangeSection } from './components/TaskChangeSection';
import { TreatmentRequestChangeSection } from './components/TreatmentRequestChangeSection';

/** Push-style side panel that slides in and compresses the main content. */
const DetailPanel: React.FC = () => {
  const { isOpen, content, contentKey, closeDrawer } = useDetailDrawer();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset scroll when content changes
  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, [contentKey]);

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden transition-all duration-300 ease-in-out',
        isOpen ? 'w-[560px]' : 'w-0',
      )}
    >
      <div
        className={cn(
          'w-[560px] fixed top-14 bottom-0 pt-4 pr-4 pb-4 transition-all duration-300 ease-in-out',
          isOpen ? 'right-0' : '-right-[560px]',
        )}
      >
        <div ref={scrollRef} className="bg-white border border-gray-200 rounded-lg h-full overflow-y-auto no-scrollbar shadow-sm">
          <div className="sticky top-0 z-10 flex items-center justify-end px-3 py-2 bg-white border-b border-gray-100">
            <button
              type="button"
              onClick={closeDrawer}
              className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
          {content}
        </div>
      </div>
    </div>
  );
};

export const MatterUpdatesDetailPage: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useEntityChangesMatterDetailQuery(
    { matterId: matterId ?? '' },
    { skip: !matterId },
  );

  const { data: matterDetails } = useGetMatterDetailsQuery(matterId ?? '', {
    skip: !matterId,
  });

  const [bulkUpdateStatus] = useEntityChangesBulkUpdateStatusMutation();

  // Optimistic local overrides for entity change statuses
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, EntityChangeStatus>
  >({});

  const handleStatusChange = useCallback(
    (entityChangeId: string, newStatus: EntityChangeStatus) => {
      // Optimistic update
      setStatusOverrides((prev) => ({ ...prev, [entityChangeId]: newStatus }));

      bulkUpdateStatus({
        bulkEntityChangeStatusRequestSchema: {
          entity_change_ids: [entityChangeId],
          action: newStatus,
        },
      }).catch(() => {
        // Revert on failure
        setStatusOverrides((prev) => {
          const next = { ...prev };
          delete next[entityChangeId];
          return next;
        });
      });
    },
    [bulkUpdateStatus],
  );

  const actionsContextValue = useMemo<EntityChangeActionsContextValue>(
    () => ({
      onStatusChange: handleStatusChange,
      statusOverrides,
    }),
    [handleStatusChange, statusOverrides],
  );

  // Toggle to show/hide entities already acted on (transferred / skipped)
  const [showCompleted, setShowCompleted] = useState(false);

  const isPending = useCallback(
    (e: EntityChangeSchema) => {
      const effective = statusOverrides[e.id as string] ?? e.status;
      return effective === 'pending';
    },
    [statusOverrides],
  );

  const filterCompleted = useCallback(
    (entities: EntityChangeSchema[], keepEntityIds?: Set<string>) => {
      if (showCompleted) return entities;
      return entities.filter(
        (e) => isPending(e) || (keepEntityIds?.has(e.entity_id) ?? false),
      );
    },
    [showCompleted, isPending],
  );

  // Parent entity IDs that still have pending children — keep those parents
  // visible even if the parent itself has been transferred/skipped.
  const parentIdsWithPendingChildren = useMemo(() => {
    const ids = new Set<string>();

    // Task updates → parent task entity IDs
    (data?.task_updates ?? [])
      .filter(isPending)
      .forEach((tu) => {
        const snapshot =
          tu.change_type === 'deleted' ? tu.old_snapshot : tu.new_snapshot;
        const taskId = (snapshot?.data as Record<string, unknown>)?.task_id as
          | string
          | undefined;
        if (taskId) ids.add(taskId);
      });

    // Treatment requests → parent provider entity IDs
    (data?.treatment_requests ?? [])
      .filter(isPending)
      .forEach((tr) => {
        const snapshot =
          tr.change_type === 'deleted' ? tr.old_snapshot : tr.new_snapshot;
        const providerId = (snapshot?.data as Record<string, unknown>)
          ?.provider_id as string | undefined;
        if (providerId) ids.add(providerId);
      });

    return ids;
  }, [data?.task_updates, data?.treatment_requests, isPending]);

  // Separate orphaned treatment requests (not associated with a listed provider)
  const providerEntityIds = useMemo(
    () => new Set((data?.providers ?? []).map((p) => p.entity_id)),
    [data?.providers],
  );

  const orphanedTreatmentRequests = useMemo(
    () =>
      (data?.treatment_requests ?? []).filter((tr) => {
        const snapshot =
          tr.change_type === 'deleted' ? tr.old_snapshot : tr.new_snapshot;
        const providerId = (snapshot?.data as Record<string, unknown>)
          ?.provider_id as string | undefined;
        return !providerId || !providerEntityIds.has(providerId);
      }),
    [data?.treatment_requests, providerEntityIds],
  );

  // Pending document entity change IDs for bulk actions from comparison panel
  const pendingDocumentIds = useMemo(
    () =>
      filterCompleted(data?.documents ?? [])
        .filter((d) => d.change_type !== 'ignored')
        .map((d) => d.id as string),
    [data?.documents, filterCompleted],
  );

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500">Failed to load matter change details.</p>
        <button
          type="button"
          onClick={() => navigate('/matter-updates')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Back to list
        </button>
      </div>
    );
  }

  // Exclude context-only (ignored) entities from counts
  const allEntities = [
    ...data.providers,
    ...data.treatment_requests,
    ...data.coverages,
    ...data.tasks,
    ...data.task_updates,
    ...data.staff_notes,
    ...data.documents,
    ...(data.case_expenses ?? []),
  ].filter((e) => e.change_type !== 'ignored');
  const totalEntities = allEntities.length;
  const pendingEntities = allEntities.filter(
    (e) => e.status === 'pending',
  ).length;

  return (
    <DetailDrawerProvider>
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="flex gap-6 items-start">
          {/* Main content - shrinks when panel opens */}
          <div className="min-w-0 flex-1 max-w-5xl mx-auto space-y-6">
            {/* Back nav + header */}
            <div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Matter Updates
              </button>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-lg font-semibold text-gray-900">
                      <MatterLink entityId={matterId ?? ''}>
                        {data.matter_name}
                      </MatterLink>
                    </h1>
                    <div className="mt-1 flex items-center gap-3">
                      <p className="text-sm text-gray-500">
                        {data.firm_name} &middot; {pendingEntities}{' '}
                        {pendingEntities === 1 ? 'entity' : 'entities'} with pending
                        changes{totalEntities !== pendingEntities && ` (${totalEntities} total)`}
                      </p>
                      {matterDetails?.external_cms_url && (
                        <a
                          href={matterDetails.external_cms_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Open in CMS
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <span className="text-xs text-gray-500">Show completed</span>
                    <Switch
                      color="zinc"
                      checked={showCompleted}
                      onChange={setShowCompleted}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Entity sections */}
            <EntityChangeActionsProvider value={actionsContextValue}>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-8">
                {pendingEntities === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">
                    No pending entity changes for this matter.
                  </p>
                )}

                <EntitySyncAccordionSection
                  matterId={matterId ?? ''}
                  firmId={matterDetails?.firm?.id}
                />

                <ProviderChangeSection
                  providers={filterCompleted(data.providers, parentIdsWithPendingChildren)}
                  treatmentRequests={filterCompleted(data.treatment_requests)}
                  tasks={data.tasks}
                  matterId={matterId ?? ''}
                />
                {orphanedTreatmentRequests.length > 0 && (
                  <TreatmentRequestChangeSection
                    treatmentRequests={filterCompleted(orphanedTreatmentRequests)}
                    matterId={matterId ?? ''}
                  />
                )}
                <CoverageChangeSection
                  coverages={filterCompleted(data.coverages)}
                  tasks={data.tasks}
                  matterId={matterId ?? ''}
                />
                <TaskChangeSection
                  tasks={filterCompleted(data.tasks, parentIdsWithPendingChildren)}
                  taskUpdates={filterCompleted(data.task_updates)}
                  matterId={matterId ?? ''}
                />
                <StaffNoteChangeSection staffNotes={filterCompleted(data.staff_notes)} />
                <CaseExpenseChangeSection
                  caseExpenses={filterCompleted(data.case_expenses ?? [])}
                />
                <DocumentChangeSection
                  documents={filterCompleted(data.documents)}
                  matterId={matterId ?? ''}
                  documentEntityChangeIds={pendingDocumentIds}
                  blobSinkEnabled={data.blob_sink_enabled}
                />
              </div>
            </EntityChangeActionsProvider>
          </div>

          {/* Push-style detail panel */}
          <DetailPanel />
        </div>
      </div>
    </DetailDrawerProvider>
  );
};
